import {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {CellColors} from 'notebook/tables/constants/CellConstants';
import {getSpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {TableEditableSpreadsheetState} from 'notebook/tables/store/TableStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

/**
 * Per-property overrides that let a caller supply a value directly instead of deriving it from
 * `SpreadsheetStore`. Each field mirrors one field of {@link TableEditableSpreadsheetState}; when
 * set it wins over the store-derived value. As more spreadsheet-derived state moves into
 * {@link TableEditableSpreadsheetState}, add the matching override here.
 */
export interface TableSpreadsheetStoreOverrides {
  overrideShowExcessLabels?: boolean;
  overrideCellColors?: CellColors;
}

/**
 * Derives the {@link TableEditableSpreadsheetState} that {@link TableModelProvider} pushes onto the
 * table store from `SpreadsheetStore`, keeping that subscription logic out of the provider. Each
 * piece of state is sourced from the spreadsheet store (scoped to `tableNamespace`) and can be
 * overridden by the caller via `overrides`.
 *
 * Isolating this here means a formula interaction re-renders only the models that read
 * `spreadsheetState` (axis-cell models for `showExcessLabels`, individual cell models for
 * `cellColors`) rather than the whole table.
 */
export function useTableSpreadsheetStore(
  tableNamespace: string,
  tableName: string,
  disableFormulas: boolean,
  documentName: string | undefined,
  {overrideShowExcessLabels, overrideCellColors}: TableSpreadsheetStoreOverrides
): TableEditableSpreadsheetState {
  // Both `showExcessLabels` and this table's `cellColors` are derived from a single
  // namespace-scoped read of the SpreadsheetStore; see `getSpreadsheetStoreState`.
  const selectSpreadsheetState = useCallback((): TableEditableSpreadsheetState => {
    // Namespace-scoped, then document-scoped. `showExcessLabels` is a per-namespace signal (a
    // formula being edited anywhere in the namespace turns it on for every table). The returned
    // `cellColors` map covers every table in the (namespace, document) pair — a formula can
    // reference and color cells in a table other than the one being edited, so we must NOT pass a
    // `tableName` (that filters `getState` down to the actively-edited table and empties everything
    // else). `documentName`, by contrast, only indexes the per-document `cellColors` map after
    // `getState` returns, so passing it correctly scopes colors to this document (formulas write
    // colors keyed by the editing document's name) without emptying anything. `disableFormulas`
    // mirrors the HOC's per-editor `hasFormulasDisabled`, which gates the formula-derived values off.
    const storeState = getSpreadsheetStoreState(tableNamespace, disableFormulas, undefined, documentName);
    return {
      showExcessLabels: storeState.showExcessLabels,
      // Slice the document's cross-table colors down to this table.
      cellColors: storeState.cellColors[tableName],
    };
  }, [tableNamespace, tableName, disableFormulas, documentName]);
  // A plain object return, so pass `isEqual` to avoid re-deriving a new reference on every store
  // change when the sliced values are unchanged.
  const storeSpreadsheetState = useStoreSelector(SpreadsheetStore, selectSpreadsheetState, isEqual);

  // Fold in the prop overrides OUTSIDE the store selector: when provided they win over the store
  // (ProtocolProcedure, StructuredTableOutsideNotebook, SimpleTable default, etc.). This must live
  // in a `useMemo` rather than inside the selector because `useStoreSelector` only re-runs on a
  // SpreadsheetStore change event — an override prop changing without a store event (the way a test
  // or a controlled caller flips the value) would otherwise be missed.
  return useMemo<TableEditableSpreadsheetState>(
    () => ({
      showExcessLabels: overrideShowExcessLabels ?? storeSpreadsheetState.showExcessLabels,
      cellColors: overrideCellColors ?? storeSpreadsheetState.cellColors,
    }),
    [overrideShowExcessLabels, overrideCellColors, storeSpreadsheetState]
  );
}
